import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import {
  blogQuery,
  blogSlugsQueryPreview,
  blogSlugsQueryProduction
} from '@lib/queries';
import { usePreviewSubscription } from '@lib/sanity';
import singlePageProps from '@lib/singlePageProps';
import { getClient, overlayDrafts, sanityClient } from '@lib/sanity.server';
import { isProduction } from 'utils';
import {
  LayoutContainer,
  PreviewBadge,
  Socials,
  BlogPostCard
} from '@components';
import { SaneBlockChakra } from '@components/sanity/sane-block';
import { SaneImage } from '@components/sanity/sane-image';
import {
  Box,
  Divider,
  Heading,
  HStack,
  Stack,
  Text,
  Icon,
  Flex,
  Grid,
  GridItem,
  Center
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import Link from 'next/link';
import { useEffect } from 'react';
import { analytics } from 'pages';

type Props = {
  data: any;
  preview: boolean;
};

export default function BlogPage({ data = {}, preview }: Props) {
  const router = useRouter();

  const slug = data?.blog?.slug;
  const live = data?.blog?.live;
  const {
    data: { blog, suggested }
  } = usePreviewSubscription(blogQuery, {
    params: { slug },
    initialData: data,
    enabled: preview && slug
  });

  useEffect(() => {
    analytics.track(`Marketing_Blog_${data.blog.title} Viewed`, {
      surface: 'Marketing',
      location: 'Blog Post'
    });
  }, []);

  if ((!router.isFallback && !slug) || (isProduction && !live)) {
    return <ErrorPage statusCode={404} />;
  }

  const formattedDate =
    blog?.date && format(new Date(blog.date), 'MMM dd, yyyy');

  return router.isFallback ? (
    <Box>Loading</Box>
  ) : (
    <>
      <Center py="60px">
        <Flex
          w="full"
          alignItems={'center'}
          justifyContent={'space-around'}
          pb="24px"
          pt="56px"
          maxW="1420px"
          display={{ base: 'none', lg: 'inline-flex' }}
        >
          <Link legacyBehavior href="/blog" passHref>
            <HStack as="a" color={'primary.500'}>
              <Icon as={ChevronLeftIcon} />
              <Text>Back</Text>
            </HStack>
          </Link>
          <HStack gap="16px">
            <Heading
              as="h4"
              size="sm"
              color="primary.500"
              bg="primary.25"
              maxW="fit-content"
              padding="6px 12px"
              gap="8px"
              borderRadius={'999px'}
            >
              {blog.category}
            </Heading>
            <Divider h="28px" orientation="vertical" />
            <Text pl="12px" textColor={'#626B7F'}>
              {formattedDate}
            </Text>
          </HStack>
          <Box w="60px"></Box>
          <Box>
            <Socials />
          </Box>
        </Flex>
      </Center>

      <Center>
        <LayoutContainer verticalPadding="none" maxW="776px" mb={'100px'}>
          <PreviewBadge isLive={blog.live} isTop />
          <Box display={{ base: 'inline-flex', lg: 'none' }} mt={14}>
            <Link legacyBehavior href="/blog" passHref>
              <HStack as="a" h="56px" color={'primary.500'}>
                <Icon as={ChevronLeftIcon} />
                <Text>Back</Text>
              </HStack>
            </Link>
          </Box>

          <Flex w="auto" flexDir={'column'} justifyContent="space-evenly">
            <HStack
              py="24px"
              gap="16px"
              display={{ base: 'inline-flex', lg: 'none' }}
            >
              <Heading
                as="h4"
                size="sm"
                color="primary.500"
                bg="primary.25"
                maxW="fit-content"
                padding="6px 12px"
                gap="8px"
                borderRadius={'999px'}
              >
                {blog.category}
              </Heading>
              <Divider h="28px" orientation="vertical" />
              <Text pl="12px" textColor={'#626B7F'}>
                {formattedDate}
              </Text>
            </HStack>

            <Heading lineHeight="48px" mb="24px" pt="16px">
              {blog.title}
            </Heading>

            <Stack
              flexDir={{ base: 'column', lg: 'row' }}
              justifyContent="space-between"
              alignItems={'center'}
            >
              <HStack py="24px" w="100%">
                {' '}
                <SaneImage
                  className="block w-10 h-10 rounded-full"
                  alt={blog.author.name}
                  src={blog.author.picture}
                  height={40}
                  width={40}
                  quality={100}
                />
                <Text pl="16px" pr="12px">
                  {blog.author.name}
                </Text>
              </HStack>
              <Divider w="100%" display={{ base: 'flex', lg: 'none' }} />
            </Stack>
          </Flex>

          <Box
            pt="32px"
            pb="48px"
            display={{ base: 'inline-flex', lg: 'none' }}
          >
            <Socials />
          </Box>
          <Center>
            {blog.coverImage && <SaneImage src={blog.coverImage} />}
          </Center>

          <Stack
            marginTop="24px"
            fontSize="18px"
            lineHeight="32px"
            letterSpacing="-0.02em"
          >
            {blog.excerpt && <Text py="32px">{blog.excerpt}</Text>}
          </Stack>

          <Divider />

          <Stack
            marginTop="24px"
            fontSize="18px"
            lineHeight="32px"
            letterSpacing="-0.02em"
            width={{ base: '90vw', md: '100%' }}
          >
            <SaneBlockChakra blocks={blog.body} />
          </Stack>
          {/* Divider hor*/}
          {/* <Box pt={{ base: '64px', lg: '200px' }} pb="100px" w="40vw">
            <Divider />
            <Heading py="48px" size="h3">
              Read more
            </Heading>
            <Grid
              templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
              gap="16px"
            >
              {suggested
                .map((post: any) => {
                  return (
                    <GridItem key={post.title}>
                      <BlogPostCard post={post} />
                    </GridItem>
                  );
                })
                .slice(0, 2)}
            </Grid>
          </Box> */}

          <PreviewBadge isLive={blog.live} />
        </LayoutContainer>
      </Center>
    </>
  );
}

export async function getStaticProps({ params, preview = false }: any) {
  const { props } = await singlePageProps({
    preview,
    slug: '/blog'
  });

  const defaultSeo = props.seo || {};

  const { blog, moreBlogs } = await getClient(preview).fetch(blogQuery, {
    slug: '/blog/' + params.slug
  });

  const suggestedPostsSlugs = [
    'introducing-web3-authentication',
    'nextjs-supabase-todos-with-multifactor-authentication',
    'how-httponly-cookies-help-mitigate-xss-attacks',
    'guide-JWT-authentication-JSON-Web-Tokens' // Fallback if current post is one of the above; only 3 will be picked.
  ];

  const suggestedPostsData = await Promise.all(
    suggestedPostsSlugs
      .filter(slug => slug !== params.slug)
      .slice(0, 3)
      .map(async slug => {
        const { blog } = await getClient(preview).fetch(blogQuery, {
          slug: '/blog/' + slug
        });

        return blog;
      })
  );

  return {
    props: {
      seo: {
        ...defaultSeo,
        title: blog.title,
        ogImage: blog.coverImage,
        description: blog.excerpt
      },
      preview,
      data: {
        blog,
        moreBlogs: overlayDrafts(moreBlogs),
        suggested: moreBlogs.slice(0, 3)
      }
    }
  };
}

export async function getStaticPaths() {
  const paths = await sanityClient.fetch(
    isProduction ? blogSlugsQueryProduction : blogSlugsQueryPreview
  );

  return {
    paths: paths.map((slug: string) => ({
      params: { slug: slug.replace('/blog/', '') }
    })),
    fallback: true
  };
}
